import {
  useGetNumberOfSupplierQuestionnairesByStatus,
  useGetSuppliersSpends,
  useGetSuppliersTotalSpends,
} from "../../../hooks/query/reports";
import { CURRENCY_SYMBOLS, QUESTIONNAIRE_STATUSES, showErrorToast } from "../../../utils";
import { Box, Flex, Heading, Icon, SkeletonText, Text, useBreakpointValue, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import GaugeChart from "react-gauge-chart";
import * as React from "react";
import { SUPPLIER_DASHBOARD_COLORS } from "../helpers";
import { supplierAdminDashboardYearsKeys } from "../SupplierAdminDashboard";
import { FaCalendarAlt } from "react-icons/fa";
import { QuestionIconTooltip } from "../../../components";
import { Card } from "../../../components/Card";
import { AppliedFilterIcon } from "./index";
import { H2 } from "components/text/headings";

function SpendCoverage({ isCategoriesFetching, filterCategory, categories }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierAdminDashboard",
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();

  const { data: suppliersSpends, isFetching: isSuppliersSpendsFetching } = useGetSuppliersSpends(filterCategory, {
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  const { data: suppliersTotalSpends, isFetching: isSuppliersTotalSpendsFetching } = useGetSuppliersTotalSpends(
    filterCategory,
    {
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0,
    }
  );

  const { data: questionnairesProgressData, isFetching: isProgressDataFetching } =
    useGetNumberOfSupplierQuestionnairesByStatus(filterCategory, {
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0,
    });

  const gaugeWidth = useBreakpointValue({
    base: 130,
    sm: 150,
    md: 170,
    lg: 150,
    xl: 170,
    "2xl": 160,
  });

  const gaugesContainerMinWidth = useBreakpointValue({
    sm: "600px",
    md: null,
  });

  return (
    <Card padding={5} height="100%">
      {isCategoriesFetching || isSuppliersSpendsFetching || isSuppliersTotalSpendsFetching || isProgressDataFetching ? (
        <SkeletonText mt="4" noOfLines={8} spacing="4" skeletonHeight="2" />
      ) : (
        <Box overflow={"auto"}>
          <Flex alignItems="center" marginBottom={5}>
            <Heading size="md">{t("spendCoverage")}</Heading>
            {filterCategory !== "" && <AppliedFilterIcon filterCategory={filterCategory} categories={categories} />}
          </Flex>
          {suppliersSpends &&
          suppliersTotalSpends &&
          typeof Object.values(suppliersSpends)
            .concat(Object.values(suppliersTotalSpends))
            .find((val) => val !== null) !== "undefined" ? (
            <Flex gap={2} justifyContent="center" minWidth={gaugesContainerMinWidth} overflow={"auto"}>
              {supplierAdminDashboardYearsKeys.map((yearKey) => {
                /*const gaugeValue = suppliersSpends && suppliersSpends[`totalSpend${yearKey}`] !== null &&
                typeof suppliersSpends[`totalSpend${yearKey}`] !== "undefined" &&
                suppliersTotalSpends && suppliersTotalSpends[`totalSpend${yearKey}`] !== null &&
                typeof suppliersTotalSpends[`totalSpend${yearKey}`] !== "undefined" ?
                  suppliersSpends?.[`totalSpend${yearKey}`] / suppliersTotalSpends?.[`totalSpend${yearKey}`] : 0;*/

                // Since we have spend data only for 2023, we get gauge value by dividing
                // with suppliersTotalSpends[`totalSpend2023`] as temporary solution.
                // TODO: When spend data for all years is available, exchange the following section with the one from above
                const gaugeValue =
                  suppliersSpends &&
                  suppliersSpends[`totalSpend${yearKey}`] !== null &&
                  typeof suppliersSpends[`totalSpend${yearKey}`] !== "undefined" &&
                  suppliersTotalSpends &&
                  suppliersTotalSpends[`totalSpend2023`] !== null &&
                  typeof suppliersTotalSpends[`totalSpend2023`] !== "undefined"
                    ? suppliersSpends?.[`totalSpend${yearKey}`] / suppliersTotalSpends?.[`totalSpend2023`]
                    : 0;

                console.log("gaugeValue", yearKey, gaugeValue);

                const spendValue =
                  suppliersSpends?.[`totalSpend${yearKey}`]?.toLocaleString("en-GB", { maximumFractionDigits: 2 }) ??
                  "N/A";

                const finalised =
                  questionnairesProgressData?.[yearKey]?.find((el) => el.status === QUESTIONNAIRE_STATUSES.FINALIZED)
                    ?.count ?? 0;

                const total =
                  questionnairesProgressData?.[yearKey]?.reduce((acc, o) => acc + parseInt(o.count), 0) ?? 0;

                return (
                  <Flex
                    key={`year-coverage-card-${yearKey}`}
                    border={"1px solid #ababab"}
                    borderRadius={"md"}
                    fontWeight="bold"
                    textAlign="center"
                    alignItems={"center"}
                    gap={2}
                    flexFlow={"column"}
                    width={`${gaugeWidth}px`}
                  >
                    <Flex alignItems={"center"} gap={1}>
                      <Icon as={FaCalendarAlt} inlineSize="4" blockSize="4" color={"green"} opacity="0.5" />
                      <Text>{yearKey}</Text>
                    </Flex>
                    <Box>
                      <GaugeChart
                        id={`gauge-${yearKey}`}
                        percent={gaugeValue > 1 ? 1 : gaugeValue < 0 ? 0 : gaugeValue}
                        colors={[
                          SUPPLIER_DASHBOARD_COLORS.RED,
                          SUPPLIER_DASHBOARD_COLORS.YELLOW,
                          SUPPLIER_DASHBOARD_COLORS.GREEN,
                        ]}
                        hideText={true}
                        arcWidth={0.5}
                        cornerRadius={0}
                        style={{ width: `${gaugeWidth}px` }}
                      />
                    </Box>
                    <Box>
                      <Flex alignItems="center">
                        <Text fontSize="xs" color={"#ababab"}>
                          {t("spendCovered")}
                        </Text>
                        <QuestionIconTooltip
                          label={t("asPer")}
                          ariaLabel={"as-per-year-tooltip"}
                          height={"unset"}
                          fontSize="xs"
                          iconSize={12}
                          size="xs"
                        />
                      </Flex>
                      <Text fontSize="2xl">{`${spendValue !== "N/A" ? CURRENCY_SYMBOLS.GBP : ""}${spendValue}`}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color={"#ababab"}>
                        {t("finalisedNumber")}
                      </Text>
                      <Text fontSize="2xl">
                        {finalised}/{total}
                      </Text>
                    </Box>
                  </Flex>
                );
              })}
            </Flex>
          ) : (
            <H2>{globalTranslation("noDataAvailable")}</H2>
          )}
        </Box>
      )}
    </Card>
  );
}

export default SpendCoverage;
